import { Alert, AlertIcon, Box, Checkbox, Flex, Input, Textarea } from '@chakra-ui/react'
import { useEffect, useState } from 'react'

import { FormField } from '../../../components/FormField'
import { FormModal } from '../../../components/FormModal'
import ImageCropInput from '../../../components/ImageCropInput/ImageCropInput'
import { MaskedInput } from '../../../components/MaskedInput'
import { ISelectOption, SearchableSelect } from '../../../components/Select'
import { ApiErrorShape, useHasRole, toFormErrors, useAppToast } from '../../../helpers'
import { ITeacherFields, TeacherService } from '../../../services/Teacher/TeacherService'
import { TeacherComplete, KnowledgeArea, TeachingFormat } from '../../../services/Teacher/types'

interface IProps {
  isOpen: boolean
  onClose: () => void
  teacher?: TeacherComplete
}

const baseForm: ITeacherFields = {
  name: '',
  email: '',
  phone: '',
  banner_picture: null,
  profile_picture: null,
  iam_picture: null,
  highlight_phrase: '',
  iam_text: '',
  staff_body_text: '',
  teaching_formats: [],
  knowledge_areas: [],
  enabled: true,
  show_on_forms: true
}

const baseFormErrors = {
  name: '',
  email: '',
  phone: '',
  banner_picture: '',
  profile_picture: '',
  iam_picture: '',
  highlight_phrase: '',
  iam_text: '',
  staff_body_text: '',
  teaching_formats: '',
  knowledge_areas: '',
  enabled: '',
  show_on_forms: ''
}

const teachingFormatOptions: ISelectOption<TeachingFormat>[] = [
  { value: 'IN_PERSON', label: 'Presencial' },
  { value: 'LIVE_ONLINE', label: 'Online ao Vivo' },
  { value: 'RECORDED_ONLINE', label: 'Online Gravado' }
]

const isValidEmail = (s: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s)

export function TeacherFormModal({ isOpen, onClose, teacher }: IProps) {
  const service = new TeacherService()
  const [saving, setSaving] = useState(false)
  const toast = useAppToast()
  const [form, setForm] = useState<ITeacherFields>(baseForm)
  const [knowledgeAreas, setKnowledgeAreas] = useState<KnowledgeArea[]>([])
  const [formErrors, setFormErrors] = useState(baseFormErrors)
  const hasRole = useHasRole()
  const permissions = {
    toggleTeacherStatus: hasRole('toggle_teacher_status'),
    editTeacher: hasRole('edit_teacher')
  }

  // Image preview states — managed internally by ImageCropInput

  useEffect(() => {
    if (isOpen) {
      setSaving(false)
      setFormErrors(baseFormErrors)

      // Load knowledge areas
      service.getKnowledgeAreas().then(setKnowledgeAreas)

      // Image preview states — managed internally by ImageCropInput

      setForm(
        teacher
          ? {
              name: teacher.name,
              email: teacher.email || '',
              phone: teacher.phone ?? '',
              banner_picture: null,
              profile_picture: null,
              iam_picture: null,
              highlight_phrase: teacher.highlight_phrase,
              iam_text: teacher.iam_text,
              staff_body_text: teacher.staff_body_text,
              teaching_formats: teacher.teaching_formats,
              knowledge_areas: teacher.knowledge_areas.map((area) => area.id),
              enabled: teacher.enabled,
              show_on_forms: teacher.show_on_forms ?? false
            }
          : baseForm
      )
    }
  }, [isOpen, teacher])

  const handleSave = async () => {
    // Valida e envia os valores já normalizados: um espaço acidental colado no
    // e-mail passaria no check de obrigatório e só seria recusado pela API.
    const normalizedForm = { ...form, email: form.email?.trim() ?? '', phone: form.phone?.trim() ?? '' }
    const errors = { ...baseFormErrors }

    if (!normalizedForm.email) {
      errors.email = 'O e-mail é obrigatório.'
    } else if (!isValidEmail(normalizedForm.email)) {
      errors.email = 'Informe um e-mail válido.'
    }

    if (!normalizedForm.phone) {
      errors.phone = 'O telefone é obrigatório.'
    } else if (normalizedForm.phone.replace(/\D/g, '').length < 10) {
      errors.phone = 'O telefone deve ter no mínimo 10 dígitos.'
    }

    if (errors.email || errors.phone) {
      setFormErrors(errors)
      return
    }

    setForm(normalizedForm)
    setFormErrors(baseFormErrors)
    setSaving(true)

    try {
      if (teacher) {
        await service.update(teacher.id, normalizedForm)
        toast.success('Professor salvo com sucesso')
      } else {
        const created = await service.create(normalizedForm)
        // O cupom nasce junto com o professor; só o código já em uso impede.
        if (created.coupon_conflict) {
          toast.warning(
            'Professor salvo',
            `O código ${created.coupon_conflict} já está em uso — o cupom não foi criado.`
          )
        } else if (created.coupon) {
          toast.success('Professor salvo', `Cupom ${created.coupon.code} criado com Taxa grátis e 10% de comissão.`)
        } else {
          toast.success('Professor salvo')
        }
      }
      onClose()
    } catch (err) {
      const { status, data } = (err as ApiErrorShape).response ?? {}
      // O Handler da API renderiza toda ValidationException como 400; 422 fica por garantia.
      if (status === 400 || status === 422) {
        setFormErrors(toFormErrors(baseFormErrors, data?.errors))
        return
      }

      toast.error(teacher ? 'Erro ao editar professor' : 'Erro ao adicionar professor', err)
    } finally {
      setSaving(false)
    }
  }

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title={`${teacher ? 'Editar' : 'Cadastrar'} Professor`}
      onSubmit={handleSave}
      size="wide"
      isLoading={saving}
      isConfirmDisabled={saving}
    >
      <Flex direction="column" gap={4}>
        <Flex direction="row" gap={3}>
          <Box w="70%">
            <FormField label="Nome" htmlFor="name" error={formErrors.name} isRequired>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                id="name"
                disabled={saving}
              />
            </FormField>
          </Box>

          <Box w="30%" display="flex" alignItems="center" justifyContent="center" gap={4}>
            <Checkbox
              isChecked={form.enabled}
              onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
              isDisabled={saving || (teacher && !permissions.toggleTeacherStatus)}
            >
              Ativo
            </Checkbox>
            <Checkbox
              isChecked={form.show_on_forms}
              onChange={(e) => setForm({ ...form, show_on_forms: e.target.checked })}
              isDisabled={saving || (teacher && !permissions.editTeacher)}
            >
              Exibir nos forms
            </Checkbox>
          </Box>
        </Flex>

        <Flex direction="row" gap={3}>
          <Box flex="1">
            <FormField label="E-mail" htmlFor="email" error={formErrors.email} isRequired>
              <Input
                type="email"
                inputMode="email"
                autoComplete="email"
                maxLength={255}
                value={form.email || ''}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                id="email"
                disabled={saving}
                aria-invalid={!!formErrors.email}
                aria-describedby={formErrors.email ? 'email-error' : undefined}
              />
            </FormField>
          </Box>

          <Box flex="1">
            <FormField label="Telefone" error={formErrors.phone} isRequired>
              <MaskedInput
                mask="phone"
                value={form.phone}
                onChange={(phone) => setForm({ ...form, phone })}
                isDisabled={saving}
              />
            </FormField>
          </Box>
        </Flex>

        <Box>
          <FormField label="Formatos de Ensino" error={formErrors.teaching_formats} isRequired>
            <SearchableSelect<TeachingFormat>
              isMulti
              value={form.teaching_formats ?? []}
              options={teachingFormatOptions}
              onChange={(formats) => setForm({ ...form, teaching_formats: formats })}
              placeholder="Selecione os formatos de ensino"
              entity="formato"
              isDisabled={saving}
            />
          </FormField>
        </Box>

        <Box>
          <FormField label="Áreas de Conhecimento" error={formErrors.knowledge_areas} isRequired>
            <SearchableSelect<number>
              isMulti
              value={form.knowledge_areas}
              options={knowledgeAreas.map((area) => ({ label: area.name, value: area.id }))}
              onChange={(areaIds) => setForm({ ...form, knowledge_areas: areaIds })}
              placeholder="Selecione as áreas de conhecimento"
              entity="área"
              feminine
              isDisabled={saving}
            />
          </FormField>
        </Box>

        <Flex direction="row" gap={3}>
          <Box flex="1">
            <ImageCropInput
              label="Imagem do Banner"
              isRequired
              value={form.banner_picture}
              onChange={(file) => setForm({ ...form, banner_picture: file })}
              previewHeight="150px"
              isDisabled={saving}
              error={formErrors.banner_picture ? [formErrors.banner_picture] : undefined}
            />
          </Box>

          <Box flex="1">
            <ImageCropInput
              label="Foto do Perfil"
              isRequired
              value={form.profile_picture}
              onChange={(file) => setForm({ ...form, profile_picture: file })}
              previewHeight="150px"
              isDisabled={saving}
              error={formErrors.profile_picture ? [formErrors.profile_picture] : undefined}
            />
          </Box>

          <Box flex="1">
            <ImageCropInput
              label="Imagem eu sou"
              isRequired
              value={form.iam_picture}
              onChange={(file) => setForm({ ...form, iam_picture: file })}
              previewHeight="150px"
              isDisabled={saving}
              error={formErrors.iam_picture ? [formErrors.iam_picture] : undefined}
            />
          </Box>
        </Flex>

        <Flex direction="row" gap={3}>
          <Box w="50%">
            <FormField
              label="Frase de Destaque"
              htmlFor="highlight_phrase"
              error={formErrors.highlight_phrase}
              isRequired
            >
              <Input
                value={form.highlight_phrase}
                onChange={(e) => setForm({ ...form, highlight_phrase: e.target.value })}
                id="highlight_phrase"
                disabled={saving}
                maxLength={255}
              />
            </FormField>
          </Box>

          <Box w="50%">
            <FormField label="Role (cargo)" htmlFor="staff_body_text" error={formErrors.staff_body_text} isRequired>
              <Input
                value={form.staff_body_text}
                onChange={(e) => setForm({ ...form, staff_body_text: e.target.value })}
                id="staff_body_text"
                disabled={saving}
                maxLength={255}
              />
            </FormField>
          </Box>
        </Flex>

        <Box>
          <FormField label="Texto eu sou" htmlFor="iam_text" error={formErrors.iam_text} isRequired>
            <Textarea
              value={form.iam_text}
              disabled={saving}
              height="110px"
              onChange={(e) => setForm({ ...form, iam_text: e.target.value })}
            />
          </FormField>
        </Box>

        {!teacher && (
          <Alert status="info" borderRadius="md" fontSize="sm">
            <AlertIcon />
            <Box>
              Um cupom global será criado automaticamente para este professor, com o código derivado do nome, Taxa
              grátis e 10% de comissão. Ele pode ser editado depois em &ldquo;Cupons de Desconto&rdquo;.
            </Box>
          </Alert>
        )}
      </Flex>
    </FormModal>
  )
}
